import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { Calendar } from "@/components/ui/calendar";
import { CheckCircle2, Calendar as CalendarIcon, Clock, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const SERVICES = [
  "Publishing Establishment (BMI / ASCAP / SESAC)",
  "Royalty Recovery & Collection",
  "Distribution & Placement",
  "Career Consulting",
  "Aspiring Artist Bootcamp",
  "General Inquiry",
];

function toISODate(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Booking() {
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get("email") || "";
  const auditScore = searchParams.get("audit_score");
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: prefillEmail,
    phone: "",
    service: auditScore ? "Career Consulting" : SERVICES[0],
    notes: auditScore ? `Coming from my Artist Infrastructure Audit™ (score: ${auditScore}). Please walk me through my results.` : "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const isoDate = useMemo(() => toISODate(date), [date]);

  useEffect(() => {
    if (!isoDate) {
      setSlots([]);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/bookings/available-slots`, {
          params: { date: isoDate },
        });
        setSlots(data.slots || []);
      } catch (e) {
        setSlots([]);
      }
    })();
  }, [isoDate]);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value || e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!isoDate || !slot) {
      toast.error("Please select a date and a time slot.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        ...form,
        date: isoDate,
        time_slot: slot,
      });
      setSuccess(true);
      toast.success("Booking received — we'll be in touch within 24 hours.");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-lg text-center" data-testid={TIDS.bookingSuccess}>
          <div className="w-16 h-16 mx-auto mb-6 border border-[var(--primary)]/60 bg-[var(--primary)]/10 flex items-center justify-center">
            <CheckCircle2 className="text-[var(--primary)]" size={28} strokeWidth={1.5} />
          </div>
          <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter mb-4">
            We&apos;ve got it.
          </h2>
          <p className="text-zinc-400 leading-relaxed mb-8">
            A consultant will email you within 24 hours to confirm your
            session. Check {form.email}.
          </p>
          <button
            onClick={() => {
              setSuccess(false);
              setDate(null);
              setSlot("");
              setForm({ name: "", email: "", phone: "", service: SERVICES[0], notes: "" });
            }}
            className="btn-ghost mx-auto"
          >
            Book another <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#050505] text-white">
      <Seo
        title="Book a free 30-minute consultation"
        description="Book a free 30-minute consulting session with E-Quantum. Publishing, royalty recovery, distribution, catalog administration, or general career strategy."
        path="/booking"
      />
      <section className="border-b border-white/10 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="tag mb-6">// Free consultation</div>
          <h1 className="font-heading font-black text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter max-w-5xl">
            Book a session.
            <br />
            <span className="text-[var(--primary)]">30 minutes. Free.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-zinc-300 text-lg leading-relaxed">
            Tell us where you are. We'll show you where the money is, and
            exactly how we'd help you get to it.
          </p>
        </div>
      </section>

      <section className="py-16">
        <form
          onSubmit={submit}
          className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10"
        >
          {/* Calendar */}
          <div className="lg:col-span-5 border border-white/10 bg-[#0a0a0a] p-6">
            <div className="flex items-center gap-2 mb-4">
              <CalendarIcon size={16} className="text-[var(--primary)]" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                Pick a date
              </div>
            </div>
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return d < today || d.getDay() === 0; // disable past + Sundays
              }}
              className="bg-transparent text-white"
            />

            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={16} className="text-[var(--primary)]" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                  {isoDate ? `Slots for ${isoDate}` : "Select a date to see slots"}
                </div>
              </div>
              {isoDate && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {slots.map((s) => (
                    <button
                      key={s.time}
                      type="button"
                      disabled={!s.available}
                      onClick={() => setSlot(s.time)}
                      data-testid={`slot-${s.time.replace(/[^a-z0-9]/gi, "")}`}
                      className={
                        "py-3 text-xs font-mono tracking-wider border transition-all " +
                        (slot === s.time
                          ? "bg-[var(--primary)] text-black border-[var(--primary)]"
                          : s.available
                          ? "border-white/10 text-zinc-300 hover:border-[var(--primary)] hover:text-[var(--primary)]"
                          : "border-white/5 text-zinc-700 cursor-not-allowed line-through")
                      }
                    >
                      {s.time}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-7 border border-white/10 bg-[#0a0a0a] p-8 md:p-10">
            <h3 className="font-heading font-bold text-2xl md:text-3xl tracking-tight mb-8">
              Your details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  Full name
                </label>
                <input
                  required
                  data-testid={TIDS.bookingName}
                  value={form.name}
                  onChange={update("name")}
                  className="input-gold"
                  placeholder="Jane Artist"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  Email
                </label>
                <input
                  required
                  type="email"
                  data-testid={TIDS.bookingEmail}
                  value={form.email}
                  onChange={update("email")}
                  className="input-gold"
                  placeholder="you@artist.com"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  Phone (optional)
                </label>
                <input
                  data-testid={TIDS.bookingPhone}
                  value={form.phone}
                  onChange={update("phone")}
                  className="input-gold"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  Service
                </label>
                <select
                  data-testid={TIDS.bookingService}
                  value={form.service}
                  onChange={update("service")}
                  className="input-gold"
                >
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  Tell us about your music & goals
                </label>
                <textarea
                  data-testid={TIDS.bookingNotes}
                  rows={5}
                  value={form.notes}
                  onChange={update("notes")}
                  className="input-gold"
                  placeholder="Genre, current catalogue size, what you've already set up, what you want to fix…"
                />
              </div>
            </div>

            <div className="mt-8 p-4 border border-[var(--primary)]/20 bg-[var(--primary)]/5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)] mb-1">
                Booking summary
              </div>
              <div className="text-sm text-zinc-300">
                {isoDate || "—"} {slot ? `· ${slot}` : ""}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                type="submit"
                disabled={submitting}
                data-testid={TIDS.bookingSubmit}
                className="btn-primary"
              >
                {submitting ? "Sending…" : "Lock in my session"} <ArrowUpRight size={16} />
              </button>
              <p className="text-xs text-zinc-500">
                Prefer to skip the calendar? <a href="/contact" className="text-[var(--primary)] hover:underline">Send us a message</a>.
              </p>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
}
